import { useCallback, useEffect, useState } from 'react'

import { getGenerationApiAccess } from '@/components/generationApi/apiKeysClient'
import { captureSentryError } from '@/lib/sentry'

export interface GenerationApiAccess {
  enabled: boolean
  hasActiveKeys: boolean
  isLoading: boolean
  hasError: boolean
  retry: () => void
}

type AccessResult =
  | {
      organizationId: string
      status: 'success'
      enabled: boolean
      hasActiveKeys: boolean
    }
  | {
      organizationId: string
      status: 'error'
    }

export function useGenerationApiAccess(organizationId: string | null): GenerationApiAccess {
  const [result, setResult] = useState<AccessResult | null>(null)
  const [requestVersion, setRequestVersion] = useState(0)

  const retry = useCallback(() => {
    if (!organizationId) return
    setResult(current => (current?.organizationId === organizationId ? null : current))
    setRequestVersion(version => version + 1)
  }, [organizationId])

  useEffect(() => {
    if (!organizationId) return
    let cancelled = false

    getGenerationApiAccess(organizationId)
      .then(access => {
        if (!cancelled) {
          setResult({
            organizationId,
            status: 'success',
            enabled: access.enabled,
            hasActiveKeys: access.hasActiveKeys,
          })
        }
      })
      .catch(error => {
        if (cancelled) return
        captureSentryError(error instanceof Error ? error : new Error(String(error)), {
          operation: 'load_generation_api_access',
          extra: { organizationId },
        })
        setResult({ organizationId, status: 'error' })
      })

    return () => {
      cancelled = true
    }
  }, [organizationId, requestVersion])

  if (!organizationId) {
    return { enabled: false, hasActiveKeys: false, isLoading: false, hasError: false, retry }
  }
  if (result?.organizationId !== organizationId) {
    return { enabled: false, hasActiveKeys: false, isLoading: true, hasError: false, retry }
  }
  if (result.status === 'error') {
    return { enabled: false, hasActiveKeys: false, isLoading: false, hasError: true, retry }
  }
  return {
    enabled: result.enabled,
    hasActiveKeys: result.hasActiveKeys,
    isLoading: false,
    hasError: false,
    retry,
  }
}
